import { useContext, useEffect, useMemo, useState } from "react"

import { useOverview, useViewable } from "web/hooks/data"

import { LocalizationContext, NavigationContext } from "web/contexts"

import { DialogCommands, Dialogs, useDialogModal } from "web/hooks/modals"

import useAsyncError from "web/hooks/behaviour/useAsyncError"

import routes from "web/routes/constants"

import { pluralize } from "web/services/inflector"

import { useUnitSettingsModal } from "../ViewSettings"
import { UseOverviewUtils } from "web/utils/hooks/useOverview.utils"

function fieldToCondition(field) {
  return {
    id: field.key,
    field_id: field.id,
    operator: "equal",
    type: "View::FieldCondition",
  }
}

export default function useCurrentView() {
  const { subjectType, viewGroup, viewId } = useContext(NavigationContext)
  const localizationContext = useContext(LocalizationContext)
  const throwError = useAsyncError()

  const [page, setPage] = useState(1)

  const currentViewType = viewId ? "view" : "group"
  const currentViewId = viewId || viewGroup.id

  const viewable = useViewable({ type: currentViewType, id: currentViewId })
  const overviewFetchParams = UseOverviewUtils.prepareViewableRequestProperties(
    viewable.type,
    viewable.id,
    [viewable.swrKey]
  )

  const overview = useOverview({
    overviewFetchParams,
  })

  useEffect(() => {
    setPage(1)
  }, [viewable.id, viewable.type])

  const isViewGroup = viewable.type === "group"
  const calculatedViewCurrencyCode =
    viewable.currency?.code ?? localizationContext.viewCurrency

  //  Calculated values
  const viewLocalizationContextValue = useMemo(() => {
    return {
      ...localizationContext,
      viewCurrency: calculatedViewCurrencyCode,
    }
  }, [calculatedViewCurrencyCode, localizationContext])

  const mapCategoryFieldId = viewable.map_category_field_id

  const { fields: fieldsWithOrdinal } = viewable
  const fields = fieldsWithOrdinal.map((fwo) => fwo.field)

  // View Actions
  const updateViewUnits = async ({
    currencyCode,
    areaUnitCode,
    lengthUnitCode,
    distanceUnitCode,
  }) => {
    const updatedUnits = viewable.update({
      currency_code: currencyCode,
      area_unit_code: areaUnitCode,
      length_unit_code: lengthUnitCode,
      distance_unit_code: distanceUnitCode,
    })

    overview.refreshViewableOverviewAndTiles()

    return updatedUnits
  }

  const handleUpdateViewUnits = useUnitSettingsModal({
    handleUpdateUnits: updateViewUnits,
    currencyCode: viewable.currency?.code,
    areaUnitCode: viewable.area_unit?.code,
    lengthUnitCode: viewable.length_unit?.code,
    distanceUnitCode: viewable.distance_unit?.code,
  })

  async function handleViewShare() {
    const turbo_frame = document.querySelector("#modal")
    if (viewGroup.parent_subject_type === "Market") {
      turbo_frame.src = `/views/${viewable.id}/markets/sharing/edit`
    } else {
      turbo_frame.src = `/views/${viewable.id}/sharing/edit`
    }
  }

  async function handlePipelineStatistics() {
    const turbo_frame = document.querySelector("#modal")
    const viewable_type = isViewGroup ? "ViewGroup" : "View"
    if (viewable_type === "ViewGroup") {
      turbo_frame.src = `/deals/${viewGroup.slug}/pipeline_statistics/new`
    } else {
      turbo_frame.src = `/deals/${viewGroup.slug}/${viewable.id}/pipeline_statistics/new`
    }
  }

  const prompt = isViewGroup
    ? "You are changing this view for all users. The fields you selected for filters will be saved, but any values selected will not. Would you like to continue?"
    : "This view is shared with other users. They will see your changes the next time they select this view. Would you like to continue?"

  const confirmUpdateModal = useDialogModal(
    Dialogs.ConfirmUpdate({ kind: viewable.name, prompt })
  )
  async function handleViewUpdate() {
    try {
      if (isViewGroup || ["teams", "account"].includes(viewable.visibility)) {
        const dialogResult = await confirmUpdateModal()
        if (dialogResult && dialogResult.response) {
          viewable.saveChanges()
        }
      } else {
        viewable.saveChanges()
      }
    } catch (error) {
      throwError(error)
    }
  }

  async function handleViewReset() {
    try {
      await viewable.reset()
      overview.refreshViewableOverviewAndTiles({ reset: true })
    } catch (error) {
      throwError(error)
    }
  }

  const saveAsModal = useDialogModal({
    title: "Save View As",
    prompt: "What would you like to call this view?",
    commands: [{ label: "Save", response: "submit" }, DialogCommands.Cancel],
    input: true,
    isInputRequired: true,
  })

  async function handleViewSaveAs() {
    const dialogResult = await saveAsModal()

    if (dialogResult && dialogResult.response) {
      try {
        const newView = await viewable.saveAs(dialogResult.inputValue)

        if (viewGroup.parent_subject_type === "Market") {
          window.Turbo.visit(
            routes.market_view(
              viewGroup.parent_subject_slug,
              subjectType,
              newView.id,
              viewGroup.topic_name
            )
          )
        } else {
          window.Turbo.visit(
            routes.view(
              pluralize(subjectType),
              viewGroup.slug,
              newView.id,
              viewGroup.topic_name
            )
          )
        }
      } catch (error) {
        throwError(error)
      }
    }
  }

  const renameModal = useDialogModal({
    title: "Rename View",
    prompt: "What would you like to rename this view?",
    commands: [{ label: "Rename", response: "submit" }, DialogCommands.Cancel],
    input: true,
    inputPlaceholder: viewable.name,
    isInputRequired: true,
  })

  async function handleViewRename() {
    const dialogResult = await renameModal()

    if (dialogResult && dialogResult.response && dialogResult.inputValue) {
      try {
        await viewable.update({ name: dialogResult.inputValue })

        dispatchCurrentViewMutateEvent()
      } catch (error) {
        throwError(error)
      }
    }
  }

  const confirmDeleteModal = useDialogModal(Dialogs.ConfirmDelete("view"))

  async function handleViewDelete() {
    const dialogResult = await confirmDeleteModal()
    if (dialogResult && dialogResult.response) {
      await viewable.destroy()

      if (viewGroup.parent_subject_type === "Market") {
        window.Turbo.visit(
          routes.market_view(
            viewGroup.parent_subject_slug,
            subjectType,
            null,
            viewGroup.topic_name
          )
        )
      } else {
        window.Turbo.visit(
          routes.view(
            pluralize(subjectType),
            viewGroup.slug,
            null,
            viewGroup.topic_name
          )
        )
      }
    }
  }

  async function handleChangeMapCategory(mapCategoryFieldId) {
    try {
      await viewable.update({
        map_category_field_id: mapCategoryFieldId || null,
      })
    } catch (error) {
      throwError(error)
    }
  }

  async function handleRowsPerPageChanged(rowsPerPage) {
    try {
      await viewable.updateTable({ rows_per_page: rowsPerPage })
      setPage(1)
    } catch (error) {
      throwError(error)
    }
  }

  async function handleSortChanged(sortField, sortDesc) {
    const field = fields.find((f) => f.key === sortField)

    try {
      await viewable.updateTable({
        sort_field_id: field.id,
        sort_direction: sortDesc ? "DESC" : "ASC",
      })
      setPage(1)
    } catch (error) {
      throwError(error)
    }
  }

  async function removeGrouping() {
    await viewable.updateTable({
      group_field_id: null,
      group_direction: null,
      second_group_field_id: null,
      second_group_direction: null,
    })
  }

  async function setGroupDirection(grouping, newGroupDesc) {
    const groupDirection = newGroupDesc ? "DESC" : "ASC"
    await viewable.updateTable({ [`${grouping}_direction`]: groupDirection })
  }

  async function setGroupField(grouping, newGroupField) {
    const field = fields.find((f) => f.key === newGroupField)

    const update = {
      [`${grouping}_field_id`]: field.id,
      [`${grouping}_direction`]: "ASC",
    }

    // clear second group when setting first group
    if (grouping === "group") {
      update.second_group_field_id = null
    }

    await viewable.updateTable(update)
  }

  async function handleGroupChanged(newGroupField) {
    const { table } = viewable

    try {
      if (newGroupField === null) {
        await removeGrouping()
        return
      }

      if (table.group_field_key === newGroupField) {
        await setGroupDirection("group", !(table.group_direction === "DESC"))
      } else if (table.second_group_field_key === newGroupField) {
        await setGroupDirection(
          "second_group",
          !(table.second_group_direction === "DESC")
        )
      } else if (!!table.second_group_field_key || !table.group_field_key) {
        await setGroupField("group", newGroupField)
      } else {
        await setGroupField("second_group", newGroupField)
      }

      setPage(1)
    } catch (error) {
      throwError(error)
    }
  }

  // Filter condition actions, these could be in a seperate hook but they're very linked to the view
  // so for now we've just put them in here

  async function handleAddFieldToConditions(field) {
    try {
      await viewable.conditions.add(fieldToCondition(field))
      overview.refreshViewableOverviewAndTiles()
    } catch (error) {
      throwError(error)
    }
  }

  async function handleAddDistanceToConditions(object) {
    try {
      await viewable.conditions.add(object)
      overview.refreshViewableOverviewAndTiles()
    } catch (error) {
      throwError(error)
    }
  }

  async function handleAddCondition(conditionToAdd) {
    try {
      await viewable.conditions.add(conditionToAdd)
      overview.refreshViewableOverviewAndTiles()
      setPage(1)
    } catch (error) {
      throwError(error)
    }
  }

  async function handleChangeCondition(
    condition,
    updatedConditionValue,
    type = "View::FieldCondition"
  ) {
    try {
      await condition.update({
        ...updatedConditionValue,
        type,
      })
      setPage(1)
      overview.refreshViewableOverviewAndTiles()
    } catch (error) {
      throwError(error)
    }
  }

  async function handleChangeDistanceCondition(
    condition,
    updatedConditionValue,
    type = "View::SpatialTypeDistanceCondition"
  ) {
    try {
      await condition.update({
        ...updatedConditionValue,
        type,
      })
      setPage(1)
    } catch (error) {
      throwError(error)
    }
  }

  async function handleDeleteCondition(condition) {
    try {
      await condition.destroy()
      setPage(1)
      overview.refreshViewableOverviewAndTiles()
    } catch (error) {
      throwError(error)
    }
  }

  function dispatchCurrentViewMutateEvent() {
    const currentViewMutateEvent = new CustomEvent("currentViewMutate")
    document.dispatchEvent(currentViewMutateEvent)
  }

  function handleFrozenFieldChanged(column) {
    if (column.isFrozen) {
      viewable.unfreezeField()
    } else {
      viewable.freezeField(column.fieldId)
    }
  }

  return {
    view: {
      ...viewable,
      isViewGroup,
      sortField: viewable.table.sort_field_key,
      sortDesc: viewable.table.sort_direction === "DESC",
      groupField: viewable.table.group_field_key,
      groupDesc: viewable.table.group_direction === "DESC",
      secondGroupField: viewable.table.second_group_field_key,
      secondGroupDesc: viewable.table.second_group_direction === "DESC",
      mapCategoryFieldId,
      viewCurrency: calculatedViewCurrencyCode, // Needed by object list
      parentSubjectType: viewGroup.parent_subject_type,
    },
    viewGroup,
    page,
    setPage,
    viewLocalizationContextValue,
    handleSortChanged,
    handleRowsPerPageChanged,
    handleFrozenFieldChanged,
    handleGroupChanged,
    handleUpdateViewUnits,
    handleViewShare,
    handlePipelineStatistics,
    handleViewUpdate,
    handleViewReset,
    handleViewRename,
    handleViewSaveAs,
    handleViewDelete,
    handleAddFieldToConditions,
    handleAddDistanceToConditions,
    handleAddCondition,
    handleChangeCondition,
    handleDeleteCondition,
    handleChangeDistanceCondition,
    handleChangeMapCategory,
    overview,
  }
}
